import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Wheat, Shirt, HardHat, HeartPulse, PawPrint, Cpu, Factory, Fuel } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useIndustries, usePage } from '../lib/cms';
import { industryExtra, getServiceBySlug } from '../data/mock';
import SEO from '../lib/SEO';

const iconMap = { Wheat, Shirt, HardHat, HeartPulse, PawPrint, Cpu, Factory, Fuel };

function MiniList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1.5">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2 text-[0.9rem] text-aqc-ink/80">
          <CheckCircle2 size={14} className="text-aqc-red mt-1 shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Industries() {
  const industries = useIndustries();
  const { content: page } = usePage('industries');
  const hero = page.hero || {};
  return (
    <div className="font-sans-body">
      <SEO />
      <section className="relative overflow-hidden">
        <div className="container-aqc relative py-16 lg:py-20 text-center">
          <span className="eyebrow">{hero.eyebrow || 'Industries We Serve'}</span>
          <h1 className="mt-5 font-serif text-[2.4rem] sm:text-[3rem] leading-[1.08] font-semibold text-aqc-navy max-w-3xl mx-auto">
            {hero.title || 'Sector expertise.'} {hero.italic && <span className="italic text-aqc-red">{hero.italic}</span>}
          </h1>
          <p className="mt-6 text-aqc-muted max-w-2xl mx-auto">
            {hero.subtitle || 'Eight sectors. Distinct standards, distinct failure modes. We bring the right checklist to your product.'}
          </p>
        </div>
      </section>

      {industries.map((ind, i) => {
        const Icon = iconMap[ind.icon] || Factory;
        const flip = i % 2 === 1;
        const extra = industryExtra[ind.id] || {};
        return (
          <section key={ind.id} id={ind.id} className={`py-16 ${i % 2 === 0 ? 'bg-white' : 'bg-aqc-cream'}`}>
            <div className="container-aqc">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={`${flip ? 'lg:order-2' : ''}`}>
                  <div className="text-[0.72rem] uppercase tracking-[0.18em] text-aqc-red font-semibold">Sector {String(i + 1).padStart(2, '0')}</div>
                  <div className="mt-4 inline-flex h-12 w-12 rounded-full bg-aqc-navy text-white items-center justify-center"><Icon size={20} /></div>
                  <h2 className="mt-4 font-serif text-3xl text-aqc-navy">{ind.title}</h2>
                  {extra.intro && <p className="mt-3 text-aqc-ink/85 leading-relaxed">{extra.intro}</p>}
                  <p className="mt-3 text-aqc-muted leading-relaxed">{ind.long}</p>
                  <ul className="mt-5 space-y-2">
                    {(ind.bullets || []).map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[0.95rem] text-aqc-ink/85">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-aqc-red shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(ind.tags || []).map((t) => (
                      <span key={t} className="text-[0.72rem] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 rounded-full bg-aqc-navy/8 text-aqc-navy">{t}</span>
                    ))}
                  </div>
                </div>
                <div className={`${flip ? 'lg:order-1' : ''}`}>
                  <img src={ind.image} alt={ind.title} className="w-full h-[380px] object-cover rounded-2xl shadow-[0_30px_60px_-30px_rgba(15,42,71,0.4)]" />
                </div>
              </div>

              {/* Extended content grid */}
              <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {extra.risks && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Common quality risks</h3>
                    <MiniList items={extra.risks} />
                  </div>
                )}
                {extra.items && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Typical items covered</h3>
                    <MiniList items={extra.items} />
                  </div>
                )}
                {extra.checkpoints && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Possible inspection checkpoints</h3>
                    <MiniList items={extra.checkpoints} />
                  </div>
                )}
                {extra.packaging && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Packaging &amp; labelling considerations</h3>
                    <MiniList items={extra.packaging} />
                  </div>
                )}
                {extra.documentation && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Documentation considerations</h3>
                    <MiniList items={extra.documentation} />
                  </div>
                )}
                {extra.relatedServices && (
                  <div className="bg-white border border-aqc-line rounded-2xl p-6">
                    <h3 className="font-serif text-lg text-aqc-navy">Relevant AQC services</h3>
                    <ul className="mt-2 space-y-1.5">
                      {extra.relatedServices.map((rs) => {
                        const svc = getServiceBySlug(rs);
                        if (!svc) return null;
                        return (
                          <li key={rs}>
                            <Link to={`/services/${rs}`} className="text-aqc-red font-semibold text-sm hover:underline flex items-center gap-1">
                              {svc.title} <ArrowRight size={14} />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Per-industry CTA */}
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 bg-aqc-navy text-white rounded-2xl p-6">
                <div className="text-white/85">
                  Need an inspection or audit for {ind.title.toLowerCase()}? Speak to a coordinator today.
                </div>
                <Link to={`/contact?industry=${ind.id}`}>
                  <Button className="bg-aqc-red hover:brightness-110 text-white h-10 px-5">Request a Quote <ArrowRight size={14} className="ml-1" /></Button>
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-16 bg-aqc-navy text-white">
        <div className="container-aqc text-center">
          <h2 className="font-serif text-3xl">Don’t see your sector?</h2>
          <p className="mt-3 text-white/75 max-w-xl mx-auto">Tell us about your product and we’ll assemble the right inspection or audit programme.</p>
          <div className="mt-6">
            <Link to="/contact"><Button className="bg-aqc-red hover:brightness-110 text-white h-11 px-6">Tell us about your product <ArrowRight size={16} className="ml-1" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
